import { styled } from "@mui/material/styles";

import type { Hbml } from "@holibob-packages/hbml";

import { ProductStartTimeListNode } from "../../apiHooks/useProduct";
import { Button } from "../../components/Button";
import { Typography } from "../../components/Typography";
import { ProductStartPlaceFragment } from "../../gql-request";
import { useNextTranslation } from "../../hooks";
import { Link } from "../../navigation/Link";
import { ProductContentHbmlRenderer } from "./ProductContentHbmlRenderer";
import { ProductSectionTitle } from "./ProductSectionTitle";
import { ProductSectionStartTimeEntries } from "./ProductStartTimeEntries";

const GOOGLE_MAPS_BASE_URL = "https://www.google.com/maps/search/";

const LocationContainer = styled("div")(({ theme }) => ({
    display: "grid",
    gap: theme.spacing(3),
    [theme.breakpoints.up("md")]: {
        gridTemplateColumns: "min-content auto",
    },
}));

const LocationMapImage = styled("img")(({ theme }) => ({
    width: "100%",
    height: 282,
    cursor: "pointer",
    objectFit: "cover",
    borderRadius: theme.roundness,
    [theme.breakpoints.up("md")]: {
        width: 360,
        height: 360,
    },
}));

const LocationDetails = styled("div")(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
    alignItems: "flex-start",
}));

const LocationStartTimes = styled("div")(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
}));

const OpenGoogleMapButton = styled(Button)(({ theme }) => ({
    borderRadius: theme.roundness,
    "&&": {
        color: theme.palette.primary.main,
    },
}));

const getLocationGoogleMapsUrl = (
    startPlace: Pick<ProductStartPlaceFragment, "geoCoordinate" | "googlePlaceId" | "mapImageUrl">
) => {
    const { geoCoordinate, googlePlaceId } = startPlace;
    const { latitude, longitude } = geoCoordinate;
    const coordinates = `${latitude},${longitude}`;
    const searchParams = new URLSearchParams({
        api: "1",
        query: coordinates,
        query_place_id: googlePlaceId,
    }).toString();
    return `${GOOGLE_MAPS_BASE_URL}?${searchParams}`;
};

type LocationProps = {
    startPlace: ProductStartPlaceFragment;
    startTimes?: ProductStartTimeListNode[];
    descriptionList?: Hbml[];
};

export const ProductLocation = (props: LocationProps) => {
    const { startTimes, startPlace, descriptionList } = props;
    const { mapImageUrl, formattedAddress } = startPlace;
    const withStartTimes = startTimes && startTimes.length > 0;
    const href = getLocationGoogleMapsUrl(startPlace);
    const [t] = useNextTranslation("product");

    const finalContent = descriptionList?.flatMap((x) => x) ?? [];
    return (
        <LocationContainer>
            <Link href={href} newTab={true} data-testid="location-map">
                <LocationMapImage src={mapImageUrl} alt="Meeting Point Map" />
            </Link>
            <LocationDetails>
                <ProductSectionTitle>{t("meetingPoint.label")}</ProductSectionTitle>
                {finalContent.length ? <ProductContentHbmlRenderer content={finalContent} /> : undefined}
                {withStartTimes && (
                    <LocationStartTimes data-testid="location-start-time">
                        <ProductSectionStartTimeEntries startTimes={startTimes} />
                    </LocationStartTimes>
                )}
                <div data-testid="location-address">
                    <Typography size="large">{t("address.label")}</Typography>
                    <Typography size="large" color="subtle">
                        {formattedAddress}
                    </Typography>
                </div>
                <OpenGoogleMapButton
                    data-leave-me-alone={true}
                    variant="outlined"
                    size="small"
                    LinkComponent={(props) => <Link {...props} newTab />}
                    href={href}
                >
                    {t("openGoogleMap.label")}
                </OpenGoogleMapButton>
            </LocationDetails>
        </LocationContainer>
    );
};
